/**
 * Wave 41 · Community Launch Party — 24h countdown + first-100 welcome bonus (10 AT).
 * Route: /atc/launch-party
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import CountdownTimer from '../../components/CountdownTimer';
import './atc.css';

export default function LaunchParty() {
  const { user } = useAuth() || {};
  const [status, setStatus] = useState(null);
  const [myClaim, setMyClaim] = useState(null);
  const [refStats, setRefStats] = useState({ referrals_made: 0, ref_bonus_earned: 0 });
  const [claiming, setClaiming] = useState(false);

  // Wave 42 · read ?ref= from URL
  const refParam = React.useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('ref'); }
    catch (_) { return null; }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/atc/launch-party');
      setStatus(r.data);
      if (user?.id) {
        try {
          const m = await api.get('/atc/launch-party/my-slot');
          setMyClaim(m.data.my_claim);
          setRefStats({ referrals_made: m.data.referrals_made || 0, ref_bonus_earned: m.data.ref_bonus_earned || 0 });
        } catch (_) { /* silent */ }
      }
    } catch (_) { /* silent */ }
  }, [user?.id]);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const claim = async () => {
    if (!user?.id) { toast.error('Login karo Bhai, phir slot lock hoga'); return; }
    setClaiming(true);
    try {
      const url = refParam ? `/atc/launch-party/claim?ref=${encodeURIComponent(refParam)}` : '/atc/launch-party/claim';
      const r = await api.post(url);
      setMyClaim(r.data.claim);
      if (r.data.referral_bonus_at) {
        toast.success(`🎉 Slot #${r.data.slot_number} locked · +10 AT bonus + 🎯 +5 AT via @${r.data.inviter_username}!`);
      } else {
        toast.success(`🎉 Slot #${r.data.slot_number} locked · 10 AT bonus credited!`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Claim failed'); }
    finally { setClaiming(false); }
  };

  // Wave 42 · personal referral link
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const myRefLink = myClaim ? `${origin}/atc/launch-party?ref=${encodeURIComponent(myClaim.user_username || '')}` : '';

  const shareText = myClaim
    ? `🎉 I just got Slot #${myClaim.slot_number} of 100 in Anant Trade Center's Launch Party — free 10 AT bonus! Join via my link (we BOTH get +5 AT extra): ${myRefLink}`
    : `🚀 Anant Trade Center launch is LIVE! First 100 users get 10 AT bonus + a shareable Founder badge. Claim your slot: ${window.location.href}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const twHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;

  if (!status) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">⏳ Loading…</div>;
  const percentTaken = (status.slots_taken / status.slots_total) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-fuchsia-950 to-slate-950 text-white p-4 sm:p-8 relative overflow-hidden"
         data-testid="atc-launch-party-page">
      {/* Floating emoji particles */}
      <div className="absolute inset-0 pointer-events-none">
        {['🎉','🎊','✨','🎈','🥳','🎇'].map((e, i) => (
          <span key={i} className="absolute text-3xl opacity-30 atc-launch-float"
                style={{ left: `${(i * 17) % 100}%`, top: `${(i * 23) % 100}%`, animationDelay: `${i * 0.7}s` }}>{e}</span>
        ))}
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        <div className="text-center mb-6">
          <div className="inline-block px-3 py-1 rounded-full bg-rose-500/30 border border-rose-400/60 text-[10px] font-black uppercase tracking-widest animate-pulse mb-3">
            {status.is_live ? '🔴 LIVE NOW' : '⏱ EVENT OVER'}
          </div>
          <h1 className="text-4xl sm:text-6xl font-black mb-2 bg-gradient-to-r from-fuchsia-300 via-yellow-200 to-amber-300 bg-clip-text text-transparent">
            🎉 Community Launch Party
          </h1>
          <p className="text-sm sm:text-base opacity-80 max-w-xl mx-auto">
            Pehle 100 users ko <b className="text-emerald-300">10 AT welcome bonus</b> + shareable founder badge.
            Free hai. Ends in 24 hours or 100 slots — jo pehle aaye!
          </p>
        </div>

        {/* Live countdown */}
        <div className="rounded-2xl p-5 bg-slate-950/60 border-2 border-fuchsia-400/50 shadow-lg shadow-fuchsia-500/20 mb-4 text-center">
          <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-300 mb-2">⏰ Party ends in</div>
          <div className="flex justify-center">
            <CountdownTimer expiresAt={status.live_until} />
          </div>
        </div>

        {/* Progress bar */}
        <div className="rounded-2xl p-4 bg-slate-950/60 border border-emerald-400/40 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-black text-emerald-300" data-testid="atc-launch-slots-taken">
              {status.slots_taken} / {status.slots_total} slots claimed
            </div>
            <div className="text-xs opacity-80 font-black">
              {status.slots_remaining} left · 🎁 10 AT each
            </div>
          </div>
          <div className="w-full h-4 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-fuchsia-400 transition-all duration-700 relative"
                 style={{ width: `${percentTaken}%` }}>
              <div className="absolute inset-0 bg-white/20 animate-pulse" />
            </div>
          </div>
        </div>

        {/* Claim CTA / My Badge */}
        {myClaim ? (
          <div className="rounded-2xl p-6 text-center bg-gradient-to-br from-amber-500/30 to-fuchsia-500/20 border-2 border-amber-300 shadow-lg shadow-amber-500/30 mb-4"
               data-testid="atc-launch-my-badge">
            <div className="text-6xl mb-2">👑</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-200 mb-1">You're a founder!</div>
            <div className="text-3xl font-black mb-1">Slot #{myClaim.slot_number}</div>
            <div className="text-xs opacity-80 mb-3">@{myClaim.user_username || 'founder'} · <span className="text-emerald-300 font-black">+10 AT credited</span></div>

            {/* Wave 42 · Referral chain box */}
            <div className="rounded-xl bg-slate-950/60 border border-emerald-400/40 p-3 mb-3 text-left">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-300 mb-1">🎯 Your Referral Link</div>
              <div className="flex items-center gap-1">
                <input readOnly value={myRefLink} data-testid="atc-launch-ref-link"
                       onClick={(e) => e.target.select()}
                       className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[11px] font-mono truncate" />
                <button onClick={() => { navigator.clipboard.writeText(myRefLink); toast.success('🔗 Link copied — share karo Bhai!'); }}
                        data-testid="atc-launch-ref-copy"
                        className="px-3 py-1 rounded bg-emerald-500 text-slate-900 text-[11px] font-black hover:brightness-110">
                  Copy
                </button>
              </div>
              <div className="text-[10px] opacity-80 mt-2">
                Har naya slot via aapka link → aap + wo dono ko <b className="text-emerald-300">+5 AT extra</b>.
              </div>
              {refStats.referrals_made > 0 && (
                <div className="mt-2 text-[11px] font-black text-emerald-300" data-testid="atc-launch-ref-stats">
                  🔗 {refStats.referrals_made} invite{refStats.referrals_made !== 1 ? 's' : ''} · ₹{refStats.ref_bonus_earned} AT earned so far!
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-center flex-wrap">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="atc-launch-share-wa"
                 className="px-4 py-2 rounded-xl bg-green-500 text-white text-sm font-black hover:brightness-110">💬 Share on WhatsApp</a>
              <a href={twHref} target="_blank" rel="noreferrer" data-testid="atc-launch-share-tw"
                 className="px-4 py-2 rounded-xl bg-sky-500 text-white text-sm font-black hover:brightness-110">🐦 Tweet it</a>
              <button onClick={() => { navigator.clipboard.writeText(shareText); toast.success('Text copied — paste anywhere'); }}
                      data-testid="atc-launch-share-copy"
                      className="px-4 py-2 rounded-xl bg-slate-700 text-white text-sm font-black hover:brightness-110">🔗 Copy Message</button>
              <a href="/atc/founders" data-testid="atc-launch-wall-link"
                 className="px-4 py-2 rounded-xl bg-amber-500 text-slate-900 text-sm font-black hover:brightness-110">🏛️ Founder Wall</a>
            </div>
          </div>
        ) : status.is_live ? (
          <>
            {refParam && (
              <div className="mb-3 rounded-xl bg-emerald-500/20 border border-emerald-400/40 p-3 text-center text-xs font-black"
                   data-testid="atc-launch-ref-banner">
                🎯 Invited by <span className="text-emerald-300">@{refParam}</span> — you both get +5 AT extra on claim!
              </div>
            )}
            <button onClick={claim} disabled={claiming} data-testid="atc-launch-claim-btn"
                    className="w-full px-4 py-4 rounded-2xl bg-gradient-to-r from-fuchsia-400 via-rose-400 to-amber-400 text-slate-900 text-xl font-black shadow-2xl shadow-fuchsia-500/40 hover:brightness-110 disabled:opacity-50 atc-launch-cta-pulse">
              {claiming ? '⏳ Locking your slot…' : `🎁 Claim Slot #${status.slots_taken + 1} · Free 10 AT${refParam ? ' +5 AT ref' : ''}`}
            </button>
          </>
        ) : (
          <div className="rounded-2xl p-6 text-center bg-slate-900/70 border border-slate-700">
            <div className="text-3xl mb-2">😢</div>
            <div className="text-lg font-black">Party is over</div>
            <div className="text-xs opacity-80 mt-1">
              {status.slots_remaining === 0 ? 'All 100 slots claimed — thank you Bharat!' : 'Countdown ended · come to the next drop 🚀'}
            </div>
            <a href="/atc/founders" className="inline-block mt-3 px-4 py-2 rounded-xl bg-amber-500 text-slate-900 text-sm font-black">🏛️ See Founder Wall</a>
          </div>
        )}

        <div className="mt-6 text-center text-[10px] opacity-60">
          Anant Trade Center · Bharat's live escrow-safe trading community
        </div>
      </div>
    </div>
  );
}
